import { useHistory } from "@myloc/myloc-utils";
import { useCallback, useEffect, useState } from "react";
import { useTranslate } from "../../language/i18n";
import productService from "../../services/product/productService";
import { decodeParam } from "../../utils/decodeParam";
import { encodeParam } from "../../utils/encodeParam";
import pages from "../../utils/pages";
import Page from "../shared/Page/Page";
import Paginate from "../shared/Pagination/Paginate";
import ProductCatalogue from "./ProductCatalogue/ProductCatalogue";
import ProductFilter from "./ProductFilter/ProductFilter";

export const VIEWS = {
  GRID: "grid",
  LIST: "list",
};

const productFilters = [
  { id: "facilities", title: "MUNICIPALITY" },
  { id: "baseStorages", title: "BASE_STORAGE_AVAILABILITY" },
  { id: "itemClasses", title: "ITEM_CLASS" },
  { id: "isoCodes", title: "ISO_CODE" },
  { id: "suppliers", title: "SUPPLIER" },
  { id: "productStatuses", title: "PRODUCT_STATUS" },
  { id: "includeNotOrderable", title: "NOT_ORDERABLE", include: false },
  { id: "isFixedAsset", title: "IS_FIXED_ASSET", include: false },
  { id: "hasAvailableBalance", title: "HAS_AVAILABLE_BALANCE", include: false },
];

// Converts encoded product filter params to usable filters
const getProductFiltersFromParams = params => {
  const reducedFilters = productFilters.reduce(
    (filters, val) => ({
      ...filters,
      [val.id]: {
        id: decodeParam(params.get(val.include)) || decodeParam(params.get(val.id)) || [],
        title: val.title,
        include: decodeParam(params.get(val.id[0])) || val.include,
      },
    }),
    {},
  );

  if (
    reducedFilters.includeNotOrderable?.id?.length &&
    JSON.parse(reducedFilters.includeNotOrderable?.id[0]) !== reducedFilters.includeNotOrderable?.include
  ) {
    reducedFilters.includeNotOrderable.include = JSON.parse(reducedFilters.includeNotOrderable?.id[0]);
  }
  return reducedFilters;
};

function ProductsPage() {
  const history = useHistory();
  const translate = useTranslate();
  const params = new URLSearchParams(window.location.search);
  const [data, setData] = useState([]);
  const [showFilter, setShowFilter] = useState(false);
  const [productFilters, setProductFilters] = useState(getProductFiltersFromParams(params));
  const [searchValueBeforeSearch, setSearchValueBeforeSearch] = useState(params.get("q") || "");
  const [searchValueForSearch, setSearchValueForSearch] = useState(params.get("q") || undefined);

  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const productFiltersChanged = (params, filters) => {
      if (!filters) return false;
      return Object.entries(filters).some(([key, value]) => params.get(key) != encodeParam(value));
    };

    if (!searchValueBeforeSearch) {
      params.delete("q");
    } else if (searchValueBeforeSearch !== params.get("q")) {
      params.set("q", searchValueBeforeSearch);
    }

    if (productFiltersChanged(params, productFilters)) {
      Object.entries(productFilters).forEach(([key, value]) => {
        if (key === "includeNotOrderable") {
          value.include ? params.set(key, value.include) : params.delete(key);
        } else {
          value.id?.length ? params.set(key, encodeParam(value)) : params.delete(key);
        }
      });
    }

    history.replace(pages.PRODUCTS.PATH, params);
  }, [productFilters, searchValueBeforeSearch, history]);

  const setParams = (param, newValue) => {
    const oldValue = params.get(param);

    if (oldValue === newValue) return oldValue;
    else if (oldValue && !newValue) {
      return oldValue;
    }
    if (newValue) {
      params.set(param, newValue);
      history.replace(pages.PRODUCTS.PATH, params);
      return newValue;
    } else return VIEWS.GRID;
  };

  const updateSearchValueForSearch = useCallback(() => {
    setSearchValueForSearch(searchValueBeforeSearch);
  }, [searchValueBeforeSearch]);

  const doSetProductsFilter = useCallback(
    productFilter => {
      setProductFilters(productFilter);
      updateSearchValueForSearch();
    },
    [updateSearchValueForSearch],
  );

  return (
    <Page title={translate(pages.PRODUCTS.NAME)}>
      <Paginate
        setIsLoading={setIsLoading}
        isLoading={isLoading}
        onChange={setData}
        provider={useCallback(
          filter =>
            productService.getProducts({
              facilityIds: productFilters?.facilities.id.map(facility => decodeURIComponent(facility)),
              inStockAt: productFilters?.baseStorages.id.map(storage => decodeURIComponent(storage)),
              isoCode: productFilters?.isoCodes?.id.map(iso => decodeURIComponent(iso))?.[0],
              supplier: productFilters?.suppliers?.id.map(supplier => decodeURIComponent(supplier))?.[0],
              itemClass: productFilters?.itemClasses?.id.map(itemClass => decodeURIComponent(itemClass))?.[0],
              productStatus: productFilters?.productStatuses?.id.map(status => decodeURIComponent(status))?.[0],
              includeNotOrderable: productFilters?.includeNotOrderable?.include,
              isFixedAsset: productFilters?.isFixedAsset?.include ? true : undefined,
              hasAvailableBalance: productFilters?.hasAvailableBalance?.include ? true : undefined,
              searchValue: searchValueForSearch,
              sort: "description:asc",
              ...filter,
            }),
          [
            productFilters?.baseStorages.id,
            productFilters?.facilities.id,
            productFilters?.hasAvailableBalance?.include,
            productFilters?.includeNotOrderable?.include,
            productFilters?.isFixedAsset?.include,
            productFilters?.isoCodes?.id,
            productFilters?.itemClasses?.id,
            productFilters?.productStatuses?.id,
            productFilters?.suppliers?.id,
            searchValueForSearch,
          ],
        )}
      >
        <ProductFilter
          showFilter={showFilter}
          hideFilter={() => setShowFilter(false)}
          productFilters={productFilters}
          setProductFilters={doSetProductsFilter}
        >
          <ProductCatalogue
            data={data}
            productFilters={productFilters}
            setProductFilters={doSetProductsFilter}
            showFilter={useCallback(() => setShowFilter(true), [setShowFilter])}
            searchValue={searchValueBeforeSearch}
            setSearchValue={setSearchValueBeforeSearch}
            isLoading={isLoading}
            setParams={setParams}
            doSearch={updateSearchValueForSearch}
          />
        </ProductFilter>
      </Paginate>
    </Page>
  );
}

export default ProductsPage;
